<template>
  <el-input
    ref="input"
    class="ui-input"
    :class="[`ui-input--${surface}`, { 'is-block': block, 'has-word-limit': hasWordLimit }]"
    :value="value"
    :show-word-limit="showWordLimit"
    v-bind="$attrs"
    v-on="forwardedListeners"
    @input="$emit('input', $event)"
  >
    <slot />
    <slot v-if="$slots.prefix" slot="prefix" name="prefix" />
    <slot v-if="$slots.suffix" slot="suffix" name="suffix" />
    <slot v-if="$slots.prepend" slot="prepend" name="prepend" />
    <slot v-if="$slots.append" slot="append" name="append" />
  </el-input>
</template>

<script>
export default {
  name: 'UiInput',
  inheritAttrs: false,
  props: {
    value: { type: [String, Number], default: '' },
    surface: {
      type: String,
      default: 'standard',
      validator: value => ['standard', 'quiet'].includes(value)
    },
    block: { type: Boolean, default: true },
    showWordLimit: { type: Boolean, default: false }
  },
  computed: {
    hasWordLimit () {
      return this.showWordLimit
    },
    forwardedListeners () {
      const listeners = { ...this.$listeners }
      delete listeners.input
      return listeners
    }
  },
  mounted () {
    this.syncNativeInputMode()
  },
  updated () {
    this.syncNativeInputMode()
  },
  methods: {
    syncNativeInputMode () {
      const inputMode = this.$attrs.inputmode
      const input = this.$el.querySelector('input, textarea')
      if (!input || !inputMode) return
      input.setAttribute('inputmode', inputMode)
    },
    focus () {
      if (this.$refs.input) this.$refs.input.focus()
    },
    blur () {
      if (this.$refs.input) this.$refs.input.blur()
    }
  }
}
</script>

<style lang="scss">
.ui-input.is-block {
  width: 100%;
}

.ui-input .el-input__inner,
.ui-input .el-textarea__inner {
  color: var(--ui-text-primary);
  border-color: var(--ui-control-rest-border);
  border-radius: var(--ui-radius-medium);
  background: linear-gradient(var(--ui-control-rest-background), var(--ui-control-rest-background)), var(--ui-bg-control);
  font-size: var(--ui-font-size-form-control);
  transition: border-color var(--ui-duration-fast) ease,
    background-color var(--ui-duration-fast) ease,
    box-shadow var(--ui-duration-fast) ease;
}

.ui-input .el-input__inner {
  height: var(--ui-control-height);
  line-height: var(--ui-control-height);
}

@media (hover: hover) and (pointer: fine) {
  .ui-input:not(.is-disabled) .el-input__inner:hover,
  .ui-input:not(.is-disabled) .el-textarea__inner:hover {
    border-color: var(--ui-control-hover-border);
    background: linear-gradient(var(--ui-control-hover-background), var(--ui-control-hover-background)), var(--ui-bg-control);
  }
}

.ui-input .el-input__inner:focus,
.ui-input .el-textarea__inner:focus {
  border-color: rgba(var(--ui-color-primary-rgb), .78);
  background: var(--ui-control-focus-background);
  box-shadow: 0 0 0 3px rgba(var(--ui-color-primary-rgb), .1);
}

.ui-input .el-input__inner::placeholder,
.ui-input .el-textarea__inner::placeholder {
  color: var(--ui-control-rest-placeholder);
}

.ui-input--quiet .el-input__inner:not(:focus),
.ui-input--quiet .el-textarea__inner:not(:focus) {
  background: rgba(var(--ui-bg-control-rgb), .72);
}

.ui-input.is-disabled .el-input__inner,
.ui-input.is-disabled .el-textarea__inner {
  color: var(--ui-text-disabled);
  border-color: var(--ui-border-color);
  background: rgba(var(--ui-bg-control-rgb), .5);
}

.ui-input .el-input-group__append,
.ui-input .el-input-group__prepend {
  color: var(--ui-text-secondary);
  border-color: var(--ui-border-color);
  background: var(--ui-bg-elevated);
}

/* 字数统计由公共输入组件统一着色，避免 Element 默认白底破坏深色界面。 */
.ui-input .el-input__count,
.ui-input .el-input__count .el-input__count-inner {
  color: var(--ui-text-muted);
  background: transparent;
  font-size: var(--ui-font-size-caption);
  line-height: 1;
  pointer-events: none;
}

.ui-input.has-word-limit .el-textarea__inner {
  padding-right: 58px;
  padding-bottom: 26px;
}
</style>
